import { Article, ArticleLang } from '../types/article';
import generatedArticles from '../data/articles.generated.json';

/**
 * Narrows the TranslationContext's `lang` (typed as a wider `string` while a
 * parallel task leaves src/locales/*.json temporarily mismatched across
 * languages — see CLAUDE-facing task notes) down to ArticleLang. Safe at
 * runtime: the provider only ever sets 'ro' | 'hu' | 'en'.
 */
export function toArticleLang(lang: string): ArticleLang {
  if (lang === 'ro' || lang === 'en') return lang;
  return 'hu';
}

// The generated file is produced by scripts/build-articles.js (wired into
// "prestart" / "prebuild"). If it hasn't been generated yet (e.g. a fresh
// checkout before the first `npm start`/`npm run build`), fall back to an
// empty list rather than crashing the app.
const ALL_ARTICLES: Article[] = Array.isArray(generatedArticles)
  ? (generatedArticles as Article[])
  : [];

/**
 * All articles for a given language, newest first.
 */
export function getArticles(lang: ArticleLang): Article[] {
  return ALL_ARTICLES.filter((article) => article.lang === lang).sort((a, b) =>
    b.date.localeCompare(a.date)
  );
}

/**
 * A single article by slug + language, or undefined if it doesn't exist.
 */
export function getArticleBySlug(slug: string, lang: ArticleLang): Article | undefined {
  return ALL_ARTICLES.find((article) => article.slug === slug && article.lang === lang);
}

/**
 * Up to 2 other articles in the same practice area and language, excluding
 * the given article itself.
 */
export function getRelatedArticles(article: Article, lang: ArticleLang): Article[] {
  return ALL_ARTICLES.filter(
    (candidate) =>
      candidate.lang === lang &&
      candidate.practiceArea === article.practiceArea &&
      candidate.slug !== article.slug
  ).slice(0, 2);
}

/**
 * Maps the current URL to its equivalent in another language.
 *
 * Article slugs are language-specific ("radiere-ipoteca-legala-sulta" in
 * Romanian, "meddig-kovetelheto-penzbeli-kiegyenlites" in Hungarian), so a
 * naive prefix swap on an article URL lands on a slug that does not exist in
 * the target language. This resolves the article and follows its
 * `translations` map instead.
 *
 * Falls back gracefully: an article with no translation in the target
 * language returns that language's article list rather than a dead URL.
 */
export function translateArticlePath(pathname: string, targetLang: ArticleLang): string | null {
  const match = pathname.match(/^\/(?:(ro|hu|en)\/)?articles\/([^/]+)\/?$/);
  if (!match) return null;

  const currentLang = toArticleLang(match[1] || 'ro');
  const article = getArticleBySlug(match[2], currentLang);
  if (!article) return null;

  if (targetLang === currentLang) return `/${targetLang}/articles/${article.slug}`;

  const translatedSlug = article.translations?.[targetLang];
  return translatedSlug
    ? `/${targetLang}/articles/${translatedSlug}`
    : `/${targetLang}/articles`;
}

/**
 * Finds an article by slug in ANY language.
 *
 * Slugs are language-specific, so a slug identifies its own language. This is
 * what lets an unprefixed URL (/articles/<slug>) resolve correctly: the app
 * otherwise picks the language from the hostname, which disagrees with the
 * slug on every host that does not contain "avocat" — the Hungarian-branded
 * domain, Firebase preview channels and localhost — and the article then looks
 * missing even though the page had just rendered it.
 */
export function findArticleInAnyLanguage(slug: string): Article | undefined {
  return ALL_ARTICLES.find((article) => article.slug === slug);
}
